import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogIn, UserPlus } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { loginWithEmail, signupWithEmail } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithEmail(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signupWithEmail(signupForm.name, signupForm.email, signupForm.password);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/4511301/pexels-photo-4511301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=940"
          alt="Rural empowerment"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/85 via-[#1E3A8A]/60 to-[#E11D48]/40" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white text-[#1E3A8A] flex items-center justify-center font-heading font-bold">PS</div>
            <div>
              <div className="font-heading font-bold text-xl">padaskillinfo</div>
              <div className="text-xs opacity-90">Pithapuram Skilling Census · RTIH</div>
            </div>
          </div>
          <div>
            <h2 className="font-heading font-bold text-4xl leading-tight">
              From village fields<br />to <span className="text-[#F59E0B]">global tech</span> careers.
            </h2>
            <p className="mt-4 text-white/85 max-w-md">
              Officers and admins: track real-time skilling census data, manage registrations,
              and connect youth to jobs, AI upskilling, and incubation support.
            </p>
          </div>
          <div className="text-xs opacity-80">© Pithapuram Constituency · RTIH Incubation Centre</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center bg-paper p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white font-heading font-bold">PS</div>
            <div className="font-heading font-bold text-lg">padaskillinfo</div>
          </div>

          <div className="mb-6">
            <h1 className="font-heading font-bold text-3xl">Officer Login</h1>
            <p className="text-gray-600 text-sm mt-1">Sign in to access the census dashboard.</p>
          </div>

          <Button onClick={handleGoogle} variant="outline" className="w-full h-11 mb-4 border-gray-300" data-testid="google-login-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200" />
            <div className="text-xs text-gray-500">OR</div>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input data-testid="login-email" type="email" required value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Password</Label>
                  <Input data-testid="login-password" type="password" required value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90" data-testid="login-submit">
                  <LogIn className="w-4 h-4 mr-2" /> {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-4">
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <Label className="text-sm">Full name</Label>
                  <Input data-testid="signup-name" required value={signupForm.name}
                    onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input data-testid="signup-email" type="email" required value={signupForm.email}
                    onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Password</Label>
                  <Input data-testid="signup-password" type="password" required minLength={6} value={signupForm.password}
                    onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#F59E0B] hover:bg-[#F59E0B]/90" data-testid="signup-submit">
                  <UserPlus className="w-4 h-4 mr-2" /> {loading ? "Creating…" : "Create account"}
                </Button>
                <p className="text-xs text-gray-500 text-center">First account becomes admin automatically.</p>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-[#1E3A8A]">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
